import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Button, CardV2 as Card, Label, List, ListItem } from '@feathr/components';

import type {
  IAd,
  IAdGroup,
  IAdGroupKeyword,
  IAdGroupRowErrors,
  ISitelink,
  TAdGroupPatch,
} from '../AdStep.types';
import AdFields from './AdFields';
import KeywordFields from './KeywordFields';
import NegativeKeywordFields from './NegativeKeywordFields';
import SitelinkFields from './SitelinkFields';

import * as styles from './AdGroupEditor.css';

interface IAdGroupEditorProps {
  adGroup: IAdGroup;
  /** Per-row messages, keyed by row key. */
  rowErrors: IAdGroupRowErrors;
  /** Messages with no field to sit against, listed above the Done button. */
  adGroupErrors: string[];
  onChange: (patch: TAdGroupPatch) => void;
  onDone: () => void;
  onAddAd: () => void;
  onRemoveAd: (adIndex: number) => void;
  onUpdateAd: (adIndex: number, patch: Partial<Omit<IAd, 'key'>>) => void;
  /** The advertiser name Google shows above each ad's display URL. */
  businessName?: string;
  disabled?: boolean;
}

/**
 * The expanded editor for one ad group: its keywords, negative keywords, and sitelinks,
 * followed by the ads that share them. Every change writes through to the underlying
 * models in memory; nothing is persisted until Done.
 *
 * Ads get their own callbacks rather than travelling in an `onChange` patch, because each
 * is a separate creative and a rebuilt `ads` array would have to be diffed back by key.
 *
 * @param props - {@link IAdGroupEditorProps}.
 */
function AdGroupEditor({
  adGroup,
  rowErrors,
  adGroupErrors,
  onChange,
  onDone,
  onAddAd,
  onRemoveAd,
  onUpdateAd,
  businessName,
  disabled = false,
}: Readonly<IAdGroupEditorProps>): JSX.Element {
  const { t } = useTranslation();

  function handleKeywordsChange(keywords: IAdGroupKeyword[]): void {
    onChange({ keywords });
  }

  function handleNegativeKeywordsChange(negativeKeywords: IAdGroupKeyword[]): void {
    onChange({ negativeKeywords });
  }

  function handleSitelinksChange(sitelinks: ISitelink[]): void {
    onChange({ sitelinks });
  }

  function handleAdChange(index: number): (patch: Partial<Omit<IAd, 'key'>>) => void {
    return function (patch: Partial<Omit<IAd, 'key'>>): void {
      onUpdateAd(index, patch);
    };
  }

  function handleRemoveAd(index: number): () => void {
    return function (): void {
      onRemoveAd(index);
    };
  }

  // No card wrapper: the enclosing ExpandableSection is the card, so these are its
  // content sections. `styles.root` only carries the local width custom properties.
  return (
    <div className={styles.root}>
      {/*
       * The next three sections hold a single Label + Well pair each, and `Label` already
       * carries an 8px margin-bottom — addVerticalGap would stack a 16px gap on top of it.
       */}
      <Card.Content>
        <KeywordFields
          disabled={disabled}
          keywordErrors={rowErrors.keywords}
          keywords={adGroup.keywords}
          onChange={handleKeywordsChange}
        />
      </Card.Content>
      <Card.Content>
        <NegativeKeywordFields
          disabled={disabled}
          keywordErrors={rowErrors.keywords}
          negativeKeywords={adGroup.negativeKeywords}
          onChange={handleNegativeKeywordsChange}
        />
      </Card.Content>
      <Card.Content>
        <SitelinkFields
          disabled={disabled}
          onChange={handleSitelinksChange}
          sitelinkErrors={rowErrors.sitelinks}
          sitelinks={adGroup.sitelinks}
        />
      </Card.Content>
      {/* This section keeps addVerticalGap for its ad cards, so the label and its alert
          are grouped in a div to keep the label's margin off that gap. */}
      <Card.Content addVerticalGap={true}>
        <div>
          <Label weight={'medium'}>{t('Ads')}</Label>
          {/*
           * The remaining Ad Grant shortfalls, which are counts rather than anything a
           * single field could carry. Everything else now sits on the field it belongs to.
           */}
          {!!adGroupErrors.length && (
            <Alert
              className={styles.alert}
              title={t('This ad group is not ready to publish yet.')}
              type={'danger'}
            >
              <List>
                {adGroupErrors.map((error) => (
                  <ListItem key={error}>{error}</ListItem>
                ))}
              </List>
            </Alert>
          )}
        </div>
        {adGroup.ads.map((ad, index) => (
          <AdFields
            ad={ad}
            adErrors={rowErrors.ads[ad.key]}
            adNumber={index + 1}
            businessName={businessName}
            disabled={disabled}
            key={ad.key}
            onChange={handleAdChange(index)}
            onRemove={handleRemoveAd(index)}
            sitelinks={adGroup.sitelinks}
          />
        ))}
        <Button
          disabled={disabled}
          name={'add-ad'}
          onClick={onAddAd}
          prefix={<FontAwesomeIcon icon={faPlus} />}
        >
          {t('Add ad to this ad group')}
        </Button>
      </Card.Content>
      {!disabled && (
        <Card.Actions>
          <Button name={'done-ad-group'} onClick={onDone} type={'primary'}>
            {t('Done')}
          </Button>
        </Card.Actions>
      )}
    </div>
  );
}

export default AdGroupEditor;
